import React, { useState } from "react";
import { driveEmbed } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Check, X, Film, Loader2, MessageSquare } from "lucide-react";

// Reusable review card for both the logged-in client portal and the public shared board.
export const CreativeReviewCard = ({ c, resolve, fallbackThumb, showNameField = false, onSubmit }) => {
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(null);
  const embed = driveEmbed(c.google_drive_link);

  const cover =
    c.type === "Static" && c.image_path ? { type: "img", src: resolve(c.image_path) }
    : c.type === "Video" && embed ? { type: "video", src: embed }
    : fallbackThumb ? { type: "img", src: resolve(fallbackThumb) }
    : { type: "icon" };

  const submit = async (decision) => {
    setBusy(decision);
    await onSubmit(c.id, decision, comment, name);
    setBusy(null); setComment("");
  };

  return (
    <div data-testid={`review-card-${c.id}`} className="bg-ink-800 border border-ink-700 rounded-2xl overflow-hidden animate-fade-up">
      <div className="bg-ink-900 flex items-center justify-center overflow-hidden">
        {cover.type === "img" ? (
          <img src={cover.src} alt={c.title} className="w-full object-cover" />
        ) : cover.type === "video" ? (
          <div className="w-full aspect-video"><iframe title={c.title} src={cover.src} className="w-full h-full" allow="autoplay" allowFullScreen /></div>
        ) : (
          <div className="w-full aspect-video flex items-center justify-center text-zinc-600"><Film className="w-8 h-8" /></div>
        )}
      </div>
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-semibold text-lg">{c.title}</h3>
          <StatusBadge status={c.status} />
        </div>

        {c.feedback && c.feedback.length > 0 && (
          <div className="rounded-xl bg-ink-900 border border-ink-700 p-3 text-sm">
            <p className="text-zinc-400 flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5 text-brand" /> Last response{c.feedback[0].client_user_name ? ` — ${c.feedback[0].client_user_name}` : ""}:</p>
            <p className={c.feedback[0].decision === "Approve" ? "text-brand font-semibold" : "text-red-400 font-semibold"}>{c.feedback[0].decision}</p>
            {c.feedback[0].comment && <p className="text-zinc-300">{c.feedback[0].comment}</p>}
          </div>
        )}

        {showNameField && (
          <Input data-testid={`review-name-${c.id}`} value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Your name (optional)" className="bg-ink-900 border-ink-700 text-sm h-9" />
        )}
        <Textarea data-testid={`review-comment-${c.id}`} value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder="Add a comment (optional)…" className="bg-ink-900 border-ink-700 text-sm min-h-[70px]" />
        <div className="flex gap-2">
          <Button data-testid={`review-approve-${c.id}`} onClick={() => submit("Approve")} disabled={!!busy}
            className="flex-1 rounded-full bg-brand text-ink-900 hover:bg-brand-hover font-semibold">
            {busy === "Approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Approve</>}
          </Button>
          <Button data-testid={`review-request-${c.id}`} onClick={() => submit("Request Changes")} disabled={!!busy}
            variant="outline" className="flex-1 rounded-full border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 font-semibold">
            {busy === "Request Changes" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><X className="w-4 h-4 mr-1" /> Request changes</>}
          </Button>
        </div>
      </div>
    </div>
  );
};
